// Sender Identifier / IdentifierCode on a received XREF01 (and XREF02).
// The import stores the number in referring_doctor_sor (legacy column name) and,
// since this fix, also medcom_details.sender.{identifier, identifierCode}.
// The receiver card already shows "1516020 · sygehusafdelingsnummer"; sender
// must do the same instead of always labelling the number as SOR.

/** Official MedCom IdentifierCodeType (ValueLists.xsd) plus common SOR spellings. */
const SOR_IDENTIFIER_CODES = new Set(['sorkode', 'sor', 'sor-kode']);

export function trimIdentifierPart(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** True only when IdentifierCode is a MedCom SOR code (`sorkode`, `sor`, `SOR-kode`). */
export function isSorIdentifierCode(code: string | null | undefined): boolean {
  const raw = trimIdentifierPart(code);
  if (!raw) return false;
  return SOR_IDENTIFIER_CODES.has(raw.toLowerCase().replace(/_/g, '-'));
}

/** Same value format as the XREF01 receiver card: `identifier · identifierCode`. */
export function formatMedcomIdentifier(
  identifier: string | null | undefined,
  identifierCode: string | null | undefined,
): string | null {
  const parts = [trimIdentifierPart(identifier), trimIdentifierPart(identifierCode)].filter(
    (part): part is string => part !== null,
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Heading for the sender identifier cell.
 * Receiver uses Identifikation and puts IdentifierCode in the value. Sender used
 * to hardcode SOR-nummer; keep that wording only when the code really is SOR.
 * Missing IdentifierCode falls back to Identifikation, never SOR-nummer.
 */
export function senderIdentifierHeadingKey(
  identifierCode: string | null | undefined,
): 'referrals.field_referring_doctor_sor' | 'referrals.xref01_identifier' {
  return isSorIdentifierCode(identifierCode)
    ? 'referrals.field_referring_doctor_sor'
    : 'referrals.xref01_identifier';
}

export type SenderIdentity = {
  identifier: string | null;
  identifierCode: string | null;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Sender Identifier/IdentifierCode persisted on medcom_details (XREF01/XREF02). */
export function readStoredSenderIdentity(
  details: Record<string, unknown> | null | undefined,
): SenderIdentity {
  const sender = isPlainRecord(details) && isPlainRecord(details.sender) ? details.sender : null;
  return {
    identifier: trimIdentifierPart(typeof sender?.identifier === 'string' ? sender.identifier : null),
    identifierCode: trimIdentifierPart(
      typeof sender?.identifierCode === 'string' ? sender.identifierCode : null,
    ),
  };
}

/**
 * Prefer medcom_details.sender (has IdentifierCode). Fall back to the legacy
 * referring_doctor_sor column so older imports still show the number.
 */
export function referralSenderIdentity(referral: {
  referring_doctor_sor: string | null;
  medcom_details: Record<string, unknown>;
}): SenderIdentity {
  const stored = readStoredSenderIdentity(referral.medcom_details);
  return {
    identifier: stored.identifier ?? trimIdentifierPart(referral.referring_doctor_sor),
    identifierCode: stored.identifierCode,
  };
}
